import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ComponentTemplate, ComponentSelection } from '@/types/codeInjection';
import { ChevronDown, ChevronRight } from 'lucide-react';

interface NestedComponentSelectorProps {
  template: ComponentTemplate;
  isSelected: boolean;
  selectedSubTemplate?: string;
  onSelectionChange: (selected: boolean, subTemplate?: string) => void;
  disabled?: boolean;
}

/**
 * NestedComponentSelector - Handles components with nested sub-templates (like Image Gradient)
 */
const NestedComponentSelector: React.FC<NestedComponentSelectorProps> = ({
  template,
  isSelected,
  selectedSubTemplate,
  onSelectionChange,
  disabled = false
}) => {
  const [isExpanded, setIsExpanded] = useState(isSelected);

  const handleMainSelectionChange = (checked: boolean) => {
    if (checked && template.subTemplates && template.subTemplates.length > 0) {
      // Auto-select first sub-template when enabling the main component
      const firstSubTemplate = template.subTemplates[0].id;
      onSelectionChange(true, firstSubTemplate);
      setIsExpanded(true);
    } else {
      onSelectionChange(false);
      setIsExpanded(false);
    }
  };

  const handleSubTemplateChange = (subTemplateId: string) => {
    onSelectionChange(true, subTemplateId);
  };

  React.useEffect(() => {
    if (isSelected && !isExpanded) {
      setIsExpanded(true);
    }
  }, [isSelected, isExpanded]);

  return (
    <div className="space-y-3">
      {/* Main component selection */}
      <div className="flex items-start space-x-3">
        <Checkbox
          id={`nested-${template.id}`}
          checked={isSelected}
          disabled={disabled}
          onCheckedChange={handleMainSelectionChange}
          className="mt-3"
        />
        <Label 
          htmlFor={`nested-${template.id}`} 
          className="flex-1 cursor-pointer"
        >
          <div className={`p-3 rounded-md border transition-colors ${
            isSelected 
              ? 'bg-primary/5 border-primary/20' 
              : disabled
              ? 'bg-red-50 border-red-200 opacity-60'
              : 'hover:bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm">{template.name}</span>
              {template.subTemplates && template.subTemplates.length > 0 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    if (isSelected) {
                      setIsExpanded(!isExpanded);
                    }
                  }}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={!isSelected}
                >
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </button>
              )}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {template.description}
            </div>
            {disabled && (
              <div className="text-xs text-red-600 mt-1">
                Conflicts with selected components
              </div>
            )}
          </div>
        </Label>
      </div>

      {/* Nested sub-templates accordion */}
      {isSelected && isExpanded && template.subTemplates && template.subTemplates.length > 0 && (
        <div className="ml-8 pl-4 border-l-2 border-gray-100">
          <Accordion type="single" collapsible className="w-full" defaultValue="subtemplates">
            <AccordionItem value="subtemplates" className="border-none">
              <AccordionTrigger className="text-xs font-medium text-gray-600 uppercase tracking-wide py-2 hover:no-underline">
                Choose Gradient Type
              </AccordionTrigger>
              <AccordionContent className="pb-2">
                <RadioGroup
                  value={selectedSubTemplate || template.subTemplates[0].id}
                  onValueChange={handleSubTemplateChange}
                  className="space-y-2"
                >
                  {template.subTemplates.map((subTemplate) => (
                    <div key={subTemplate.id} className="flex items-center space-x-3">
                      <RadioGroupItem 
                        value={subTemplate.id} 
                        id={`${template.id}-${subTemplate.id}`}
                        className="text-primary"
                      />
                      <Label 
                        htmlFor={`${template.id}-${subTemplate.id}`} 
                        className="flex-1 cursor-pointer"
                      >
                        <div className={`p-2 rounded-md border transition-colors ${
                          selectedSubTemplate === subTemplate.id 
                            ? 'bg-primary/5 border-primary/20' 
                            : 'hover:bg-gray-50 border-gray-200'
                        }`}>
                          <div className="font-medium text-sm">{subTemplate.name}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            {subTemplate.description}
                          </div>
                        </div>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      )}
    </div>
  );
};

export default NestedComponentSelector;